import React from 'react';
import { useStacklineTheme, StacklineColor, hexToRgba } from '@stackline/ui';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material';
import colors from 'src/utils/colors';
import { AllCategoriesIcon } from 'src/components/SvgIcons/SvgIcons';

type CustomColor = {
  accentSand: { value: string; opacity: number };
  accentPurple: { value: string; opacity: number };
  accentBlue: { value: string; opacity: number };
  brickOrange: { value: string; opacity: number };
  graysPrimary: { value: string; opacity: number };
};

export interface ElementCardProps {
  children: React.ReactNode;
  color: StacklineColor | keyof CustomColor;
  variant?: 'large' | 'small' | 'medium' | 'extraSmall';
  textSx?: SxProps<Theme>;
  isAllCategoriesEntity?: boolean;
}

/**
 * Displays text inside of colored container, for use
 * inside of the entity card in the left nav
 */
const ElementCard = ({
  children,
  color,
  variant = 'large',
  textSx = {},
  isAllCategoriesEntity = false
}: ElementCardProps) => {
  const theme = useStacklineTheme();

  const variantSizeMapping: { [key in ElementCardProps['variant']]: string } = {
    large: '72px',
    medium: '48px',
    small: '40px',
    extraSmall: '32px'
  };

  const backgroundColorOverride: Partial<Record<StacklineColor, { value: string; opacity: number }>> | CustomColor = {
    primary: { value: '#a5cfff', opacity: 0.1 },
    info: { value: colors.lightestBlue, opacity: 1 },
    accentTangerine: { value: '#fdf2ea', opacity: 1 },
    primaryLight: { value: colors.lightestBlue, opacity: 1 },
    accentBlue: { value: '#F1F9F3', opacity: 1 },
    accentSand: { value: '#FFF8EA', opacity: 1 },
    accentPurple: { value: '#F6E8F1', opacity: 1 },
    brickOrange: { value: '#BC3C19', opacity: 0.1 },
    graysPrimary: { value: colors.graysPrimary, opacity: 1 }
  };

  const backgroundColor = backgroundColorOverride[color]
    ? hexToRgba(backgroundColorOverride[color].value, backgroundColorOverride[color].opacity)
    : hexToRgba(theme.colors[color], 0.1);

  return (
    <div
      style={{
        backgroundColor,
        width: variantSizeMapping[variant],
        height: variantSizeMapping[variant],
        padding: variant === 'extraSmall' ? '10px' : '12px 17px 12px 18px',
        borderRadius: variant === 'large' ? theme.spacing.sm : theme.spacing.xs,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
      }}
    >
      {isAllCategoriesEntity ? (
        <AllCategoriesIcon width={32} height={32} />
      ) : (
        <Text variant={variant === 'large' ? 'h1' : 'subtitle1'} color={color as StacklineColor} sx={textSx}>
          {children}
        </Text>
      )}
    </div>
  );
};

export default ElementCard;
